import { useState, useMemo, useRef, useEffect } from 'react'
import { Card, Button, Input, Select } from '@/components/ui'
import {
  buildDetectionUrl,
  buildDetectionUrlWithSourceCapture,
  buildBootstrapScriptUrl,
  DETECTION_ASSET_TYPES,
  DETECTION_DOMAINS,
} from '@spoofio/shared'

/**
 * Detect the current environment from VITE_API_URL.
 * Staging uses a separate detection domain so canaries report to the correct worker.
 */
function isStaging(): boolean {
  const apiUrl = String(import.meta.env.VITE_API_URL ?? '')
  return apiUrl.includes('staging')
}

/** Domain options - varies by environment */
const DOMAIN_OPTIONS = import.meta.env.DEV
  ? [
      { value: DETECTION_DOMAINS.LOCAL, label: 'Local Development' },
      { value: DETECTION_DOMAINS.PRODUCTION, label: 'Production (cdn.stylerender.net)' },
    ]
  : isStaging()
    ? [
        { value: DETECTION_DOMAINS.STAGING, label: 'Staging (staging-cdn.stylerender.net)' },
        { value: DETECTION_DOMAINS.PRODUCTION, label: 'Production (cdn.stylerender.net)' },
      ]
    : [
        { value: DETECTION_DOMAINS.PRODUCTION, label: 'cdn.stylerender.net' },
      ]

/** Default domain - local in dev, staging-aware in deployed builds */
const DEFAULT_DOMAIN = import.meta.env.DEV
  ? DETECTION_DOMAINS.LOCAL
  : isStaging()
    ? DETECTION_DOMAINS.STAGING
    : DETECTION_DOMAINS.PRODUCTION

/** Default asset paths for each type */
const DEFAULT_PATHS: Record<DetectionAssetType, string> = {
  css: '/assets/widget.css',
  js: '/dist/embed.js',
}

type DetectionAssetType = (typeof DETECTION_ASSET_TYPES)[number]

type EmbedFormat = 'external' | 'javascript' | 'link' | 'import'

type EmbedCodeGeneratorProps = {
  /** The canary token */
  token: string
}

type EmbedFormatInfo = {
  id: EmbedFormat
  label: string
  recommended: boolean
  description: string
}

const EMBED_FORMATS: EmbedFormatInfo[] = [
  {
    id: 'external',
    label: 'External Script (CSP-Safe)',
    recommended: true,
    description:
      'Most reliable detection. CSP-friendly — no inline script required. Add script-src for your detection domain.',
  },
  {
    id: 'javascript',
    label: 'Inline Script',
    recommended: false,
    description:
      'Captures the exact page URL. Requires unsafe-inline in CSP or a per-embed script hash.',
  },
  {
    id: 'link',
    label: 'Link Tag',
    recommended: false,
    description:
      'Simple HTML embed. More stealthy but depends on browser sending Referer header.',
  },
  {
    id: 'import',
    label: 'CSS @import',
    recommended: false,
    description:
      'For CSS-only injection points. Use when platform only allows custom CSS.',
  },
]

/**
 * EmbedCodeGenerator Component
 *
 * Generates embed codes for Component Mask detection (CSS/JS assets).
 * Provides three embed formats:
 * - JavaScript: Most reliable, captures source URL
 * - Link Tag: Simple HTML
 * - CSS @import: For CSS-only contexts
 */
type TestStatus = 'idle' | 'loading' | 'success' | 'error'

type TestResult = {
  status: TestStatus
  message: string
  details?: string
}

export function EmbedCodeGenerator({ token }: EmbedCodeGeneratorProps) {
  const [assetType, setAssetType] = useState<DetectionAssetType>('css')
  const [filePath, setFilePath] = useState(DEFAULT_PATHS.css)
  const [productName, setProductName] = useState('Widget Embed')
  const [detectionDomain, setDetectionDomain] = useState<string>(DEFAULT_DOMAIN)
  const [copiedFormat, setCopiedFormat] = useState<EmbedFormat | null>(null)
  const [testResult, setTestResult] = useState<TestResult>({
    status: 'idle',
    message: '',
  })

  // Update file path when asset type changes
  const handleAssetTypeChange = (newType: DetectionAssetType) => {
    setAssetType(newType)
    setFilePath(DEFAULT_PATHS[newType])
  }

  // Generate the detection URL
  const detectionUrl = useMemo(() => {
    return buildDetectionUrl({
      baseUrl: detectionDomain,
      path: filePath,
      token,
    })
  }, [detectionDomain, filePath, token])

  // Generate the detection URL with source capture placeholder
  const detectionUrlWithSource = useMemo(() => {
    return buildDetectionUrlWithSourceCapture({
      baseUrl: detectionDomain,
      path: filePath,
      token,
    })
  }, [detectionDomain, filePath, token])

  // Generate the bootstrap script URL (external, CSP-safe)
  const bootstrapUrl = useMemo(() => {
    // Bootstrap always uses a .js path
    const jsPath = filePath.endsWith('.js') ? filePath : '/dist/embed.js'
    return buildBootstrapScriptUrl({
      baseUrl: detectionDomain,
      path: jsPath,
      token,
    })
  }, [detectionDomain, filePath, token])

  // Generate embed codes for each format
  const embedCodes = useMemo(() => {
    // External script embed (CSP-safe) — no inline code
    const externalEmbed = `<script src="${bootstrapUrl}"></script>`

    // Inline JavaScript embed with source capture (legacy)
    const jsEmbed = `<script>
(function(){
  var l=document.createElement('link');
  l.rel='stylesheet';
  l.href='${detectionUrlWithSource}'+btoa(location.href).replace(/\\+/g,'-').replace(/\\//g,'_').replace(/=/g,'');
  document.head.appendChild(l);
})();
</script>`

    // Link tag embed
    const linkEmbed = `<link rel="stylesheet" href="${detectionUrl}">`

    // CSS @import embed
    const importEmbed = `@import url("${detectionUrl}");`

    return {
      external: externalEmbed,
      javascript: jsEmbed,
      link: linkEmbed,
      import: importEmbed,
    }
  }, [bootstrapUrl, detectionUrl, detectionUrlWithSource])

  // Clear copy feedback timer on unmount to prevent state updates after cleanup
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current)
    }
  }, [])

  // Copy to clipboard handler
  const handleCopy = async (format: EmbedFormat) => {
    try {
      await navigator.clipboard.writeText(embedCodes[format])
      setCopiedFormat(format)
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current)
      copyTimerRef.current = setTimeout(() => { setCopiedFormat(null); }, 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  // Test the detection endpoint
  const handleTest = async () => {
    setTestResult({ status: 'loading', message: 'Testing endpoint...' })

    try {
      const startTime = performance.now()
      const response = await fetch(detectionUrl, {
        method: 'GET',
        mode: 'cors',
      })
      const endTime = performance.now()
      const responseTime = Math.round(endTime - startTime)

      if (response.ok) {
        const contentType = response.headers.get('content-type') ?? 'unknown'
        const body = await response.text()
        const bodySize = new Blob([body]).size

        // Check if response looks like a valid payload
        const isValidPayload =
          (assetType === 'css' && contentType.includes('text/css')) ||
          (assetType === 'js' && contentType.includes('javascript'))

        if (isValidPayload) {
          setTestResult({
            status: 'success',
            message: `Endpoint responding correctly`,
            details: `Returned valid ${assetType.toUpperCase()} (${bodySize} bytes) in ${responseTime}ms`,
          })
        } else {
          setTestResult({
            status: 'success',
            message: `Endpoint responding`,
            details: `Content-Type: ${contentType} (${bodySize} bytes, ${responseTime}ms)`,
          })
        }
      } else {
        setTestResult({
          status: 'error',
          message: `Endpoint returned ${response.status}`,
          details: response.statusText || 'No additional details',
        })
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred'

      // Provide helpful messages for common errors
      let details = errorMessage
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        details =
          'Could not reach endpoint. Check if the detection worker is running and CORS is configured.'
      } else if (errorMessage.includes('CORS')) {
        details =
          'CORS error. The detection worker may need to allow requests from this origin.'
      }

      setTestResult({
        status: 'error',
        message: 'Connection failed',
        details,
      })
    }
  }

  return (
    <Card
      title="Embed Code Generator"
      description="Generate stealthy embed codes for your protected pages"
    >
      <div className="space-y-6">
        {/* Configuration Section */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {DOMAIN_OPTIONS.length > 1 && (
            <Select
              label="Detection Domain"
              value={detectionDomain}
              onChange={(e) => { setDetectionDomain(e.target.value); }}
              options={DOMAIN_OPTIONS}
            />
          )}
          <Select
            label="Asset Type"
            value={assetType}
            onChange={(e) =>
              { handleAssetTypeChange(e.target.value as DetectionAssetType); }
            }
            options={[
              { value: 'css', label: 'CSS Stylesheet' },
              { value: 'js', label: 'JavaScript' },
            ]}
          />
          <Input
            label="File Path"
            value={filePath}
            onChange={(e) => { setFilePath(e.target.value); }}
            placeholder="/assets/widget.css"
            helperText="Path that appears in the URL"
          />
          <Input
            label="Product Name"
            value={productName}
            onChange={(e) => { setProductName(e.target.value); }}
            placeholder="Widget Embed"
            helperText="For payload customization"
          />
        </div>

        {/* URL Preview with Test Button */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Detection URL Preview
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => { void handleTest() }}
              isLoading={testResult.status === 'loading'}
            >
              Test Endpoint
            </Button>
          </div>
          <code className="text-xs text-gray-600 break-all block mb-3">
            {detectionUrl}
          </code>

          {/* Test Result Display */}
          {testResult.status !== 'idle' && (
            <div
              className={`mt-3 p-3 rounded-md text-sm ${
                testResult.status === 'success'
                  ? 'bg-green-50 border border-green-200'
                  : testResult.status === 'error'
                    ? 'bg-red-50 border border-red-200'
                    : 'bg-blue-50 border border-blue-200'
              }`}
            >
              <div
                className={`font-medium ${
                  testResult.status === 'success'
                    ? 'text-green-800'
                    : testResult.status === 'error'
                      ? 'text-red-800'
                      : 'text-blue-800'
                }`}
              >
                {testResult.status === 'success' && '✓ '}
                {testResult.status === 'error' && '✗ '}
                {testResult.message}
              </div>
              {testResult.details && (
                <div
                  className={`mt-1 text-xs ${
                    testResult.status === 'success'
                      ? 'text-green-700'
                      : testResult.status === 'error'
                        ? 'text-red-700'
                        : 'text-blue-700'
                  }`}
                >
                  {testResult.details}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Embed Format Selection */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Embed Formats
          </h4>
          <div className="space-y-4">
            {EMBED_FORMATS.map((format) => (
              <EmbedFormatCard
                key={format.id}
                format={format}
                code={embedCodes[format.id]}
                isCopied={copiedFormat === format.id}
                onCopy={() => { void handleCopy(format.id) }}
              />
            ))}
          </div>
        </div>

        {/* Usage Tips */}
        <div className="bg-primary-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-primary-900 mb-2">
            Usage Tips
          </h4>
          <ul className="text-sm text-primary-700 space-y-1">
            <li>
              - Use <strong>JavaScript</strong> embed for best detection accuracy
            </li>
            <li>
              - URLs are designed to look like legitimate CDN assets
            </li>
            <li>
              - Change the file path to match your site&apos;s existing assets
            </li>
            <li>
              - Detection is silent and returns valid CSS/JS content
            </li>
          </ul>
        </div>
      </div>
    </Card>
  )
}

/**
 * Individual Embed Format Card
 */
function EmbedFormatCard({
  format,
  code,
  isCopied,
  onCopy,
}: {
  format: EmbedFormatInfo
  code: string
  isCopied: boolean
  onCopy: () => void
}) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{format.label}</span>
          {format.recommended && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
              Recommended
            </span>
          )}
        </div>
        <Button variant="secondary" size="sm" onClick={onCopy}>
          {isCopied ? 'Copied!' : 'Copy'}
        </Button>
      </div>
      <div className="p-4">
        <p className="text-sm text-gray-500 mb-3">{format.description}</p>
        <pre className="bg-gray-900 text-gray-100 p-3 rounded-md text-xs overflow-x-auto">
          <code>{code}</code>
        </pre>
      </div>
    </div>
  )
}

export default EmbedCodeGenerator
